import { useState } from 'react'
import { supabase } from '~/supabase-client'
import { supabaseGetter } from '~/api/supabase-getter'
import { useCurrentUser } from '~/common/use-current-user'

const PHASE_COUNT = 10
const HARVEST_COUNT = 6

const escapeCSVValue = (val) => {
  if (val == null) return ''
  const str = String(val)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

const arrayToCSV = (rows, columns) => {
  const header = columns.join(',')
  const body = rows.map(row => columns.map(col => escapeCSVValue(row[col])).join(','))
  return [header, ...body].join('\n')
}

const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const flattenClimate = (climate) => ({
  org_bounds: climate?.bounds ? JSON.stringify(climate.bounds) : '',
  zone: climate?.zone ?? '',
  avg_max_temp: climate?.avg_max_temp ?? '',
  avg_min_temp: climate?.avg_min_temp ?? '',
  avg_lfd: climate?.avg_lfd ?? '',
  avg_ffd: climate?.avg_ffd ?? '',
  avg_ffw: climate?.avg_ffw ?? '',
  avg_gdd10: climate?.avg_gdd10 ?? '',
  avg_gdd5: climate?.avg_gdd5 ?? '',
  fs_risk: climate?.fs_risk ?? '',
})

const CSV_COLUMNS = [
  'row_type',
  // org group (fully populated for row_type='org'; only org_id for children)
  'org_id', 'org_type', 'org_name', 'org_bounds',
  'zone', 'avg_max_temp', 'avg_min_temp', 'avg_lfd', 'avg_ffd', 'avg_ffw', 'avg_gdd10', 'avg_gdd5', 'fs_risk',
  // season ref
  'season_id',
  // task group
  'task_code', 'task_window_start', 'task_completed_at', 'task_notes', 'parent_org_id', 'parent_crop_id',
  // report group
  'report_code', 'report_notes',
  // crop group
  'crop_id', 'crop_name', 'crop_count', 'crop_yield_kg', 'species_name', 'variety_name', 'archetype_code', 'family_code', 'crop_notes',
  // crop schedule group
  'schedule_id', 'schedule_state',
  // phase group
  ...Array.from({ length: PHASE_COUNT }, (_, i) => i + 1).flatMap(n => [
    `phase_${n}_type`, `phase_${n}_est_started_at`, `phase_${n}_est_duration`,
    `phase_${n}_started_at`, `phase_${n}_duration`,
  ]),
  // harvest group
  ...Array.from({ length: HARVEST_COUNT }, (_, i) => i + 1).flatMap(n => [
    `harvest_${n}_at`, `harvest_${n}_yield_kg`,
  ]),
]

export const useDataExport = () => {
  const { currentUser } = useCurrentUser()
  const [exporting, setExporting] = useState(false)

  const buildExportData = async (from, to) => {
    // 1. Get user's farm memberships
    const { data: memberships, error: membershipsError } = await supabase
      .from('organization_users')
      .select('organization_id, role, created_at')
      .eq('user_id', currentUser.id)
    if (membershipsError) throw membershipsError

    const memberOrgIds = (memberships ?? []).map(m => m.organization_id)

    const { data: farmsData, error: farmsError } = await supabase
      .from('organizations')
      .select('id, name, organization_type')
      .in('id', memberOrgIds)
      .eq('organization_type', 'farm')
    if (farmsError) throw farmsError

    const farms = farmsData ?? []
    const farmIds = farms.map(f => f.id)

    // 2. For each farm: fetch plots and climate in parallel
    const farmData = await Promise.all(farms.map(async (farm) => {
      const [plotsRes, climateRes] = await Promise.all([
        supabaseGetter.organizations.getPlots(farm.id),
        supabaseGetter.climates.getByOrganization(farm.id),
      ])
      const plots = plotsRes.data ?? []
      const climate = climateRes.data ?? null

      // 3. For each plot: fetch patches
      const plotData = await Promise.all(plots.map(async (plot) => {
        const { data: patches } = await supabaseGetter.organizations.getPatches(plot.id)

        // 4. For each patch: fetch crops with all schedules and phases, date-filtered
        const patchData = await Promise.all((patches ?? []).map(async (patch) => {
          const { data: crops } = await supabase
            .from('crops')
            .select('*, crop_details(family_code, archetype_code, growth_code, lifecycle_code, notes), crop_schedules(*, season:seasons(state), crop_phases(*))')
            .eq('organization_id', patch.id)
            .gte('created_at', from)
            .lte('created_at', to)
            .order('created_at', { ascending: true })
          return { ...patch, crops: crops ?? [] }
        }))

        return { ...plot, patches: patchData }
      }))

      return { ...farm, climate, plots: plotData }
    }))

    // 5. Collect all org IDs in the hierarchy for task queries
    const allOrgIds = [
      ...farmIds,
      ...farmData.flatMap(f => f.plots.flatMap(p => [p.id, ...p.patches.map(pa => pa.id)])),
    ]

    // 6. Tasks across all orgs (org-level + crop-level), date-filtered
    const tasksResults = allOrgIds.length > 0
      ? await supabase
          .from('tasks')
          .select('*, task_types(*)')
          .in('organization_id', allOrgIds)
          .gte('created_at', from)
          .lte('created_at', to)
          .order('created_at', { ascending: true })
      : { data: [] }

    // 7. Reports per farm, date-filtered
    const reportsPerFarm = await Promise.all(farmIds.map(async (farmId) => {
      const { data } = await supabase
        .from('reports')
        .select('*, report_types(*)')
        .eq('organization_id', farmId)
        .gte('created_at', from)
        .lte('created_at', to)
        .order('created_at', { ascending: true })
      return { farmId, reports: data ?? [] }
    }))

    return {
      farmData,
      tasks: tasksResults.data ?? [],
      reportsPerFarm,
      memberships: memberships ?? [],
    }
  }

  const exportJSON = async ({ startDate, endDate }) => {
    const from = startDate.startOf('day').toISOString()
    const to = endDate.endOf('day').toISOString()
    const { farmData, tasks, reportsPerFarm, memberships } = await buildExportData(from, to)

    const reportsByFarm = Object.fromEntries(reportsPerFarm.map(r => [r.farmId, r.reports]))
    const orgTasks = tasks.filter(t => !t.crop_id)
    const cropTasks = tasks.filter(t => !!t.crop_id)

    const farms = farmData.map(farm => ({
      id: farm.id,
      name: farm.name,
      climate: farm.climate,
      plots: farm.plots.map(plot => ({
        id: plot.id,
        name: plot.name,
        tasks: orgTasks.filter(t => t.organization_id === plot.id),
        patches: plot.patches.map(patch => ({
          id: patch.id,
          name: patch.name,
          tasks: orgTasks.filter(t => t.organization_id === patch.id),
          crops: patch.crops.map(crop => {
            const activeSchedule = crop.crop_schedules?.find(s => s.season?.state === 'active') ?? crop.crop_schedules?.[0] ?? null
            const { crop_schedules: _s, ...cropFields } = crop
            return {
              ...cropFields,
              schedule: activeSchedule
                ? {
                    id: activeSchedule.id,
                    season_id: activeSchedule.season_id ?? null,
                    state: activeSchedule.season?.state ?? null,
                    created_at: activeSchedule.created_at,
                    phases: (activeSchedule.crop_phases ?? []).sort((a, b) => a.step - b.step),
                  }
                : null,
            }
          }),
        })),
      })),
      tasks: orgTasks.filter(t => t.organization_id === farm.id),
      reports: reportsByFarm[farm.id] ?? [],
    }))

    const exportData = {
      exported_at: new Date().toISOString(),
      date_range: { from, to },
      user: {
        id: currentUser.id,
        email: currentUser.email,
        username: currentUser.username,
        created_at: currentUser.created_at,
      },
      farms,
      crop_tasks: cropTasks,
      organization_memberships: memberships.map(m => ({
        organization_id: m.organization_id,
        role: m.role,
        created_at: m.created_at,
      })),
    }

    downloadFile(
      JSON.stringify(exportData, null, 2),
      `terraplan-export-${startDate.format('YYYY-MM-DD')}-to-${endDate.format('YYYY-MM-DD')}.json`,
      'application/json',
    )
  }

  const exportCSV = async ({ startDate, endDate }) => {
    const from = startDate.startOf('day').toISOString()
    const to = endDate.endOf('day').toISOString()
    const { farmData, tasks, reportsPerFarm } = await buildExportData(from, to)

    // Build orgId → farm climate lookup
    const orgFarmMap = {}
    const farmClimateMap = {}
    for (const farm of farmData) {
      orgFarmMap[farm.id] = farm.id
      farmClimateMap[farm.id] = farm.climate
      for (const plot of farm.plots) {
        orgFarmMap[plot.id] = farm.id
        for (const patch of plot.patches) {
          orgFarmMap[patch.id] = farm.id
        }
      }
    }

    // Build harvest task submissions lookup by crop_id
    const harvestSubmissionsByCrop = {}
    for (const task of tasks) {
      if (task.crop_id && task.task_code === 'harvest') {
        const subs = (task.submissions ?? []).map(s => ({
          submitted_at: s.submitted_at,
          yield_kg: s.yield_kg,
        }))
        if (!harvestSubmissionsByCrop[task.crop_id]) harvestSubmissionsByCrop[task.crop_id] = []
        harvestSubmissionsByCrop[task.crop_id].push(...subs)
      }
    }
    for (const cropId of Object.keys(harvestSubmissionsByCrop)) {
      harvestSubmissionsByCrop[cropId].sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at))
    }

    const rows = []

    // Org rows — one per farm, plot, patch (with full climate/name data)
    for (const farm of farmData) {
      const farmClimate = farmClimateMap[farm.id]
      rows.push({
        row_type: 'org',
        org_id: farm.id,
        org_type: farm.organization_type,
        org_name: farm.name,
        ...flattenClimate(farmClimate),
      })
      for (const plot of farm.plots) {
        rows.push({
          row_type: 'org',
          org_id: plot.id,
          org_type: plot.organization_type,
          org_name: plot.name,
        })
        for (const patch of plot.patches) {
          rows.push({
            row_type: 'org',
            org_id: patch.id,
            org_type: patch.organization_type,
            org_name: patch.name,
          })
        }
      }
    }

    // Task rows
    for (const task of tasks) {
      rows.push({
        row_type: 'task',
        org_id: task.organization_id,
        season_id: task.season_id ?? '',
        task_code: task.task_code ?? '',
        task_window_start: task.task_window_start ?? '',
        task_completed_at: task.completed_at ?? '',
        task_notes: task.notes ?? '',
        parent_org_id: task.crop_id ? '' : task.organization_id,
        parent_crop_id: task.crop_id ?? '',
      })
    }

    // Report rows
    for (const { farmId, reports } of reportsPerFarm) {
      for (const report of reports) {
        rows.push({
          row_type: 'report',
          org_id: farmId,
          season_id: report.season_id ?? '',
          report_code: report.report_code ?? '',
          report_notes: report.notes ?? '',
        })
      }
    }

    // Crop rows — one row per crop_schedule so all seasons are represented
    for (const farm of farmData) {
      for (const plot of farm.plots) {
        for (const patch of plot.patches) {
          for (const crop of patch.crops) {
            const harvestSubmissions = harvestSubmissionsByCrop[crop.id] ?? []
            const schedules = (crop.crop_schedules ?? []).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))

            for (const schedule of schedules) {
              const phases = (schedule.crop_phases ?? []).sort((a, b) => a.step - b.step)

              const row = {
                row_type: 'crop',
                org_id: patch.id,
                season_id: schedule.season_id ?? '',
                crop_id: crop.id,
                crop_name: crop.name ?? '',
                crop_count: crop.count ?? '',
                crop_yield_kg: crop.yield ?? '',
                species_name: crop.species_name ?? '',
                variety_name: crop.variety_name ?? '',
                archetype_code: crop.crop_details?.archetype_code ?? '',
                family_code: crop.crop_details?.family_code ?? '',
                crop_notes: crop.crop_details?.notes ?? '',
                schedule_id: schedule.id,
                schedule_state: schedule.state ?? '',
              }

              for (let n = 1; n <= PHASE_COUNT; n++) {
                const phase = phases[n - 1]
                row[`phase_${n}_type`] = phase?.phase_type_code ?? ''
                row[`phase_${n}_est_started_at`] = phase?.est_started_at ?? ''
                row[`phase_${n}_est_duration`] = phase?.est_duration ?? ''
                row[`phase_${n}_started_at`] = phase?.started_at ?? ''
                row[`phase_${n}_duration`] = phase?.duration ?? ''
              }

              for (let n = 1; n <= HARVEST_COUNT; n++) {
                const sub = harvestSubmissions[n - 1]
                row[`harvest_${n}_at`] = sub?.submitted_at ?? ''
                row[`harvest_${n}_yield_kg`] = sub?.yield_kg ?? ''
              }

              rows.push(row)
            }
          }
        }
      }
    }

    downloadFile(
      arrayToCSV(rows, CSV_COLUMNS),
      `terraplan-export-${startDate.format('YYYY-MM-DD')}-to-${endDate.format('YYYY-MM-DD')}.csv`,
      'text/csv',
    )
  }

  const handleExport = async (format, { startDate, endDate }) => {
    if (!currentUser?.id) return
    try {
      setExporting(true)
      if (format === 'json') {
        await exportJSON({ startDate, endDate })
      } else {
        await exportCSV({ startDate, endDate })
      }
    } finally {
      setExporting(false)
    }
  }

  return { handleExport, exporting }
}
